'use client';

import { Table } from '@tanstack/react-table';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo, useRef, useState } from 'react';

import { ImportProgressEvent, type XlsxColumn } from 'src/lib/xlsx/types';

import { getTableHeader } from './utils';

/**
 * 导入结果
 */
export interface ImportResult {
  /** 导入进度 0-100 */
  progress?: number;
  /** 进度消息 */
  message?: string;
  /** 错误信息 */
  error?: {
    /** 错误消息 */
    message: string;
    /** 错误详情列表 */
    details?: Array<{ message: string }>;
  };
  /** 是否成功 */
  success?: boolean;
  /** 成功数据 */
  data?: { count: number };
}

export interface UseDataTableImportOptions {
  /** 表格实例 */
  table: Table<any>;
  /** api endpoint */
  apiEndpoint?: string;
  /** 导入成功回调 */
  onSuccess?: () => void;
}

export interface UseDataTableImportReturn {
  /** 对话框打开状态 */
  open: boolean;
  /** 对话框状态变化回调 */
  onOpenChange: (open: boolean) => void;
  /** 是否正在导入 */
  loading: boolean;
  /** 导入结果 */
  result: ImportResult | undefined;
  /** 执行导入 */
  handleImport: (file: File) => Promise<void>;
  /** 下载模板 */
  handleDownloadTemplate: (filename: string) => Promise<void>;
  /** 取消导入 */
  cancelImport: () => void;
  /** 重置状态 */
  reset: () => void;
  /** 是否成功 */
  isSuccess: () => boolean;
  /** 是否错误 */
  isError: () => boolean;
  /** 是否已导入过数据（包括部分导入） */
  hasImported: () => boolean;
}

/**
 * 数据表格导入 Hook
 *
 * @example
 * ```tsx
 * const importer = useDataTableImport({
 *   table,
 *   apiEndpoint: '/api/settings/vehicle/import', // 可选，默认使用当前路径 + /import
 *   onSuccess: () => router.refresh(),
 * });
 * ```
 */
export function useDataTableImport(options: UseDataTableImportOptions): UseDataTableImportReturn {
  const pathname = usePathname();
  const { table, apiEndpoint, onSuccess } = options;
  const importUrl = apiEndpoint || `${pathname}/import`;

  const [open, setOpen] = useState(false);

  // 从 TanStack Table 获取表头信息（导入不过滤隐藏列，但过滤 unimportable 列）
  // 使用 useMemo 缓存，避免每次渲染都重新计算导致引用变化
  const header: XlsxColumn[][] = useMemo(() => getTableHeader(table, { forImport: true }), [table]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | undefined>(undefined);

  const handleResult = (newResult: ImportResult | undefined) => {
    if (newResult === undefined) setResult(undefined);
    else setResult((oldResult) => ({ ...oldResult, ...newResult }));
  };

  // 判断是否成功
  const isSuccess = useCallback(() => {
    return result?.success !== undefined;
  }, [result]);

  // 判断是否错误
  const isError = useCallback(() => {
    return result?.error !== undefined;
  }, [result]);

  // 判断是否已导入过数据
  const hasImported = useCallback(() => {
    return Boolean(result?.progress || result?.success);
  }, [result]);

  // 重置状态
  const reset = useCallback(() => {
    setLoading(false);
    handleResult(undefined);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // 处理对话框状态变化
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !loading) {
        // 关闭对话框时，如果有导入过数据，调用 onSuccess
        if (hasImported()) {
          onSuccess?.();
        }

        // 重置所有状态
        reset();
      }
      setOpen(newOpen);
    },
    [loading, hasImported, onSuccess, reset]
  );

  // 取消导入
  const cancelImport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
    // 取消时保留当前进度信息
  }, []);

  // 下载模板
  const handleDownloadTemplate = useCallback(
    async (filename: string) => {
      try {
        // 构建 FormData（不包含 file，表示下载模板）
        const formData = new FormData();
        formData.append('header', JSON.stringify(header));

        const response = await fetch(importUrl, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 下载文件
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Failed to download template:', error);
      }
    },
    [importUrl, header]
  );

  // 执行导入
  const handleImport = useCallback(
    async (file: File) => {
      // 重置状态
      setLoading(true);
      handleResult(undefined);

      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();

      try {
        // 构建 FormData（header 通过 body 传递）
        const formData = new FormData();
        formData.append('file', file);
        formData.append('header', JSON.stringify(header));

        // 发送请求
        const response = await fetch(importUrl, {
          method: 'POST',
          body: formData,
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 读取 SSE 流
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('无法读取响应流');
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // 解码数据
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter((line) => line.trim());

          for (const line of lines) {
            try {
              const event = JSON.parse(line) as ImportProgressEvent;

              if (event.type === 'info') {
                // 更新进度信息
                handleResult({
                  progress: event.progress || 0,
                  message: event.message || '',
                });
              } else if (event.type === 'success') {
                // 导入成功
                setLoading(false);
                handleResult({
                  progress: 100,
                  message: event.message || '导入成功',
                  success: true,
                  data: event.data,
                });
              } else if (event.type === 'error') {
                // 导入失败
                setLoading(false);
                handleResult({
                  error: {
                    message: event.message || '导入失败',
                    details: event.details,
                  },
                });
              }
            } catch (parseError) {
              console.error('Failed to parse SSE event:', parseError);
            }
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          // 用户取消
          return;
        }

        setLoading(false);
        handleResult({
          progress: 0,
          message: error instanceof Error ? error.message : '文件上传失败',
          error: {
            message: error instanceof Error ? error.message : '文件上传失败',
          },
        });
      }
    },
    [importUrl, header]
  );

  return {
    open,
    onOpenChange: handleOpenChange,
    loading,
    result,
    handleImport,
    handleDownloadTemplate,
    cancelImport,
    reset,
    isSuccess,
    isError,
    hasImported,
  };
}
